import ToggleGroup, { ToggleGroupOption } from "@/components/toggle-group"
import { Label } from "@/components/ui/label"
import { Icon } from "@/icons/icon"
import { useFormEmit } from "@/lib/form/form"
import { useFormContext } from "@/lib/form/form-context"
import { ParamSchema, ParamWindow } from "@/lib/form/schema"
import { FormParamStatus, WidgetBaseProps } from "@/lib/form/types"
import { useEffect, useState } from "react"
import { useWatch } from "@/lib/form/form"
import { ErrorFlagState } from "@/components/ui/input"
import { format } from "date-fns"
import { TZDate } from "@date-fns/tz"
import { cn } from "@/lib/utils"
import { TinyText } from "@/components/tiny-text"
import PendingBadge from "@/components/pending-badge"

export interface ChoiceWidgetProps extends WidgetBaseProps<ParamSchema> {
  className?: string
}

export default function ChoiceWidget({
  className,
  schema,
  path,
}: ChoiceWidgetProps) {
  const paramPath = path
  const { options, correctValue } = useOptions()
  const { form, readOnly: ctxReadOnly, sheetHasActiveBreakdown } = useFormContext()
  const emit = useFormEmit(form)

  const values = useWatch({
    form,
    name: path,
    defaultValue: {},
    deep: 3,
  })
  const flagState = useFlagState()
  const inputs = useInputs()

  function useFlagState() {
    const [flagState, setFlagState] = useState<ErrorFlagState>(
      ErrorFlagState.None
    )

    useEffect(() => {
      if (values?.value === correctValue) {
        setFlagState(ErrorFlagState.None)

        return
      }

      if (values?.remark) {
        setFlagState(ErrorFlagState.Filled)
      } else {
        setFlagState(ErrorFlagState.Unfilled)
      }
    }, [values, correctValue])

    return flagState
  }

  function getFlagState(value: any, remark?: string) {
    if (!value || value === correctValue) {
      return ErrorFlagState.None
    }

    if (remark) {
      return ErrorFlagState.Filled
    }

    return ErrorFlagState.Unfilled
  }

  interface InputDef {
    id: string
    value?: string
    status: FormParamStatus
    time: string
    flag: ErrorFlagState
    path: string
  }

  function useInputs() {
    const [inputs, setInputs] = useState<InputDef[]>([])

    useEffect(() => {
      if (!schema.window?.length) {
        setInputs([])

        return
      }

      setInputs(
        schema.window.map((window, index) => {
          const currValue = values?.window?.at(index)

          return {
            id: window.id,
            value: currValue?.value,
            status: currValue?.status || FormParamStatus.PENDING,
            time: formatWindowText(window),
            flag: getFlagState(currValue?.value, currValue?.remark),
            path: `${path}.window[${index}]`,
          }
        })
      )
    }, [schema.window, values?.window, correctValue])

    return inputs
  }

  function useOptions() {
    const [options, setOptions] = useState<ToggleGroupOption[]>([])
    const [correctValue, setCorrectValue] = useState("")

    useEffect(() => {
      if (schema.choices) {
        setOptions(
          schema.choices.map((choice) => ({
            label: choice.label,
            value: choice.value,
          }))
        )

        const correct = schema.choices.find((choice) => choice.isCorrect)
        setCorrectValue(correct?.value || "")
      } else {
        setOptions([])
      }
    }, [schema])

    return {
      options,
      correctValue,
    }
  }

  function handleChange(value: string) {
    form.setValue(
      paramPath,
      {
        id: schema.id,
        recordedAt: new Date().toISOString(),
        value: value,
        status: FormParamStatus.DRAFT,
      },
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    )
  }

  function handleWindowInputChange(value: string, index: number) {
    const windowPath = `${path}.window[${index}]`

    form.setValue(
      windowPath,
      {
        id: schema.window?.at(index)?.id,
        value: value,
        status: FormParamStatus.DRAFT,
        startTime: schema.window?.at(index)?.startTime,
        endTime: schema.window?.at(index)?.endTime,
        recordedAt: new Date().toISOString(),
      },
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    )
  }

  function handleClickRedFlag(clickedPath?: string) {
    emit("clickParamRemarks", {
      path: clickedPath || path,
      schema,
    })
  }

  function formatTime(str: string | undefined) {
    if (!str) {
      return ""
    }

    return format(new TZDate(str, "UTC"), "HH:mm")
  }

  function formatWindowText(window?: ParamWindow): string {
    if (!window) {
      return ""
    }

    return `${formatTime(window?.startTime)} - ${formatTime(window?.endTime)}`
  }

  function isDisabled(status?: FormParamStatus) {
    if (!status) {
      return false
    }

    const disabledStatuses: FormParamStatus[] = [
      FormParamStatus.NOT_APPLICABLE,
      FormParamStatus.DISABLED,
      FormParamStatus.BREAK_DOWN,
      FormParamStatus.CHANGE_OVER,
      FormParamStatus.POWER_FAILURE,
      FormParamStatus.OTHER,
    ]

    if (disabledStatuses.includes(status)) {
      return true
    }

    return false
  }

  function getSubmittedBadge(__value?: any, status?: FormParamStatus) {
    if (
      status === FormParamStatus.NOT_APPLICABLE ||
      sheetHasActiveBreakdown
    ) {
      return "NA"
    }

    return "PENDING"
  }

  function getBadgeStyles(status?: FormParamStatus) {
    if (
      status === FormParamStatus.NOT_APPLICABLE ||
      sheetHasActiveBreakdown
    ) {
      return "text-button-black border-black"
    }

    return ""
  }


  function shouldShowValue(status?: FormParamStatus, value?: string) {
    if (status === FormParamStatus.NOT_APPLICABLE) {
      return false
    }

    return !!value
  }

  function showPreview(status?: FormParamStatus) {
    if (!status || status === FormParamStatus.DRAFT) {
      return false
    }
    return true
  }

  function handleClickSubmittedErrorFlag(index?: number) {
    let emittedPath = path
    let emittedValue = values

    if (typeof index === "number") {
      emittedPath = `${emittedPath}.window[${index}]`
      emittedValue = values?.window?.at(index)
    }

    emit("clickShowRemarkDetails", {
      path: emittedPath,
      schema: schema,
      payload: emittedValue,
    })
  }

  const allPreview = inputs.length > 0 && inputs.every((input) => showPreview(input.status))

  if (inputs.length) {
    return (
      <div className={cn("flex gap-4", className)}>
        <div className="basis-2/5">
          <Label className="text-sm font-medium">{schema.name}</Label>
        </div>

        <div className={cn(
          allPreview
            ? "grid grid-cols-2 basis-3/5 gap-1 gap-y-2"
            : "flex flex-col gap-4 basis-3/5"
        )}>
          {inputs.map((input, index) =>
            showPreview(input.status) ? (
              shouldShowValue(input.status, input.value) ? (
                <div
                  key={input.id || `window-${index}`}
                  className="flex flex-col justify-evenly"
                >
                  <div className="flex font-medium items-center gap-3">
                    {input.value}

                    {input.flag === ErrorFlagState.Filled && (
                      <button
                        className="hover:cursor-pointer"
                        type="button"
                        onClick={() => handleClickSubmittedErrorFlag(index)}
                      >
                        <Icon name="RedFlag" className="size-4" />
                      </button>
                    )}

                    {input.flag === ErrorFlagState.Unfilled && (
                      <Icon name="FlagNeutral" className="size-4" />
                    )}
                  </div>
                  <TinyText text={input.time} />
                </div>
              ) : (
                <div key={input.id || `window-${index}`} className="flex flex-col gap-1">
                  <PendingBadge
                    className={getBadgeStyles(input.status)}
                    text={getSubmittedBadge(input?.value, input?.status)}
                  />

                  <TinyText text={input.time} />
                </div>
              )
            ) : (
              <ToggleGroup
                key={input.id || `window-${index}`}
                options={options}
                value={input.value}
                correctValue={correctValue}
                disabled={
                  isDisabled(input.status) ||
                  !!ctxReadOnly ||
                  !!sheetHasActiveBreakdown
                }
                helpText={input.time}
                flagState={input.flag}
                onChange={(value) => handleWindowInputChange(value, index)}
                onClickRedFlag={() => handleClickRedFlag(input.path)}
              />
            )
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={cn("flex items-center gap-4", className)}>
      <div className="basis-2/5">
        <Label className="text-sm font-medium">{schema.name}</Label>
      </div>

      {showPreview(values?.status) ? (
        shouldShowValue(values?.status, values?.value) ? (
          <div className="basis-3/5 flex font-medium text-sm items-center gap-4">
            {values.value}

            {flagState === ErrorFlagState.Filled && (
              <button
                className="hover:cursor-pointer"
                type="button"
                onClick={() => handleClickSubmittedErrorFlag()}
              >
                <Icon name="RedFlag" className="size-4" />
              </button>
            )}

            {flagState === ErrorFlagState.Unfilled && (
              <Icon name="FlagNeutral" className="size-4" />
            )}
          </div>
        ) : (
          <div className="basis-3/5">
            <PendingBadge
              className={getBadgeStyles(values?.status)}
              text={getSubmittedBadge(values?.value, values?.status)}
            />
          </div>
        )
      ) : (
        <ToggleGroup
          className="basis-3/5"
          options={options}
          value={values?.value}
          correctValue={correctValue}
          disabled={
            isDisabled(values?.status) ||
            !!ctxReadOnly ||
            !!sheetHasActiveBreakdown
          }
          flagState={flagState}
          onChange={handleChange}
          onClickRedFlag={() => handleClickRedFlag()}
        />
      )}
    </div>
  )
}
